import { api } from './api';
import { Ticket, TicketStats, CreateTicketData, TicketFilter } from '../types/ticket';

interface PaginatedResponse<T> {
    data: T[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
}

export const ticketService = {
    // User endpoints
    async getMyTickets(page = 1, perPage = 10): Promise<PaginatedResponse<Ticket>> {
        const response = await api.get('/tickets', {
            params: { page, per_page: perPage }
        });
        return {
            data: response.data.data,
            meta: response.data.meta
        };
    },

    async createTicket(data: CreateTicketData): Promise<Ticket> {
        const response = await api.post('/tickets', data);
        return response.data.ticket;
    },

    async getTicket(ticketId: number): Promise<Ticket> {
        const response = await api.get(`/tickets/${ticketId}`);
        return response.data.ticket;
    },

    async addMessage(ticketId: number, message: string): Promise<Ticket> {
        const response = await api.post(`/tickets/${ticketId}/messages`, { message });
        return response.data.ticket;
    },

    // Admin endpoints
    async getAdminTickets(filters: TicketFilter & { page?: number; perPage?: number } = {}): Promise<PaginatedResponse<Ticket>> {
        const response = await api.get('/admin/tickets', {
            params: {
                page: filters.page || 1,
                per_page: filters.perPage || 25,
                search: filters.search,
                type: filters.type,
                status: filters.status,
                priority: filters.priority,
                sort_by: filters.sortBy || 'created_at',
                sort_order: filters.sortOrder || 'desc'
            }
        });
        return {
            data: response.data.data,
            meta: response.data.meta
        };
    },

    async getAdminStats(): Promise<TicketStats> {
        const response = await api.get('/admin/tickets/stats');
        return response.data;
    },

    async getAdminTicket(ticketId: number): Promise<Ticket> {
        const response = await api.get(`/admin/tickets/${ticketId}`);
        return response.data.ticket;
    },

    async adminReply(ticketId: number, message: string): Promise<Ticket> {
        const response = await api.post(`/admin/tickets/${ticketId}/messages`, { message });
        return response.data.ticket;
    },

    async closeTicket(ticketId: number): Promise<Ticket> {
        const response = await api.post(`/admin/tickets/${ticketId}/close`);
        return response.data.ticket;
    },

    async updateStatus(ticketId: number, status: string): Promise<Ticket> {
        const response = await api.patch(`/admin/tickets/${ticketId}/status`, { status });
        return response.data.ticket;
    },

    async downloadScreenshot(messageId: number): Promise<void> {
        const response = await api.get(`/tickets/messages/${messageId}/screenshot`);
        if (response.data.url) {
            window.open(response.data.url, '_blank');
        }
    }
};
